/**
 * LANDING PAGE COMPONENT
 * =====================
 * 
 * This is the main homepage that visitors see before signing up or logging in.
 * It's designed to convert visitors into users by explaining the value proposition
 * and guiding them through the signup process.
 * 
 * STRUCTURE:
 * - Header with navigation and theme toggle
 * - Hero section with main value proposition
 * - Features section showcasing key benefits
 * - Pricing section with clear tiers
 * - Testimonials for social proof
 * - Footer with additional links and resources
 * 
 * The page is fully responsive and includes smooth scroll navigation between sections.
 */

import { useState } from "react";
import { Header } from "./Header";
import { Hero } from "./Hero";
import { HowTo } from "./HowTo";
import { Features } from "./Features";
import { Pricing } from "./Pricing";
import { Testimonials } from "./Testimonials";
import { Footer } from "./Footer";
import { VideoPlayerPopup } from "./VideoPlayerPopup";

interface LandingPageProps {
  onSignIn: () => void;                           // Navigate to sign-in page
  onSignUp: () => void;                           // Navigate to sign-up page
  onScrollToSection: (sectionId: string) => void; // Smooth scroll to sections
  
  // Footer navigation props - Resources section
  onNavigateToBlog: () => void;                   // Navigate to blog
  onNavigateToHelpCenter: () => void;             // Navigate to help center
  onNavigateToBestPractices: () => void;          // Navigate to best practices
  
  // Footer navigation props - Company section
  onNavigateToAbout: () => void;                  // Navigate to about page
  onNavigateToPrivacy: () => void;                // Navigate to privacy policy
  onNavigateToTerms: () => void;                  // Navigate to terms of service
  onNavigateToContact: () => void;                // Navigate to contact page
}

export function LandingPage({
  onSignIn,
  onSignUp,
  onScrollToSection,
  onNavigateToBlog,
  onNavigateToHelpCenter,
  onNavigateToBestPractices,
  onNavigateToAbout,
  onNavigateToPrivacy,
  onNavigateToTerms,
  onNavigateToContact,
}: LandingPageProps) {
  const [isVideoDemoOpen, setIsVideoDemoOpen] = useState(false);

  const handleOpenDemo = () => {
    setIsVideoDemoOpen(true);
  };

  const handleCloseDemo = () => {
    setIsVideoDemoOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* HEADER - Navigation with theme toggle */}
      <Header 
        onSignIn={onSignIn}
        onSignUp={onSignUp}
        onScrollToSection={onScrollToSection}
        onOpenDemo={handleOpenDemo}
      />
      
      {/* MAIN CONTENT */}
      <main>
        {/* HERO SECTION - Main value proposition */}
        <Hero 
          onSignUp={onSignUp}
          onScrollToSection={onScrollToSection}
          onOpenDemo={handleOpenDemo}
        />
        
        {/* HOW TO SECTION - 3-step process */}
        <HowTo />
        
        {/* FEATURES SECTION - Key benefits and functionality */}
        <section id="features">
          <Features onSignUp={onSignUp} />
        </section>
        
        {/* PRICING SECTION - Plans and pricing tiers */}
        <section id="pricing">
          <Pricing onSignUp={onSignUp} />
        </section>
        
        {/* TESTIMONIALS SECTION - Social proof */}
        <section id="testimonials">
          <Testimonials />
        </section>
      </main>
      
      {/* FOOTER - Additional links and resources */}
      <Footer 
        onNavigateToBlog={onNavigateToBlog}
        onNavigateToHelpCenter={onNavigateToHelpCenter}
        onNavigateToBestPractices={onNavigateToBestPractices}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToContact={onNavigateToContact}
        onScrollToSection={onScrollToSection}
        onOpenDemo={handleOpenDemo}
      />
      
      {/* VIDEO DEMO POPUP */}
      <VideoPlayerPopup
        isOpen={isVideoDemoOpen}
        onClose={handleCloseDemo}
        videoUrl="https://youtu.be/KAOnkw3V5V8"
      />
    </div>
  );
}